import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography } from "@mui/material";
import { Close, LockOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import BackdropOverlay from "../common/BackdropOverlay";
import useLobbySettingsController from "./useLobbySettingsController";
import {
    closeLobbySettingsPanel,
    setLobbySettingsPanelTab,
    useLobbySettingsPanel,
} from "../../stores/lobbySettingsPanelStore";
import {
    LOBBY_SETTINGS_TABS,
    resolveLobbySettingsTab,
} from "./lobbySettingsTabs";
import MapPoolTab from "./MapPoolTab";
import ServerLocationsTab from "./ServerLocationsTab";
import MatchFiltersTab from "./MatchFiltersTab";
import { shouldShowPremiumMatchFiltersPrompt } from "./premiumMatchFiltersGate";
import SettingsSelectionMeter from "./SettingsSelectionMeter";
import { normalizeLocationId } from "../lobby/lobbyPageUtils";
import {
    MOBILE_NAV_MQ,
    SETTINGS_CONTENT_COLUMN_SX,
    SETTINGS_DRAWER_BACKDROP_TRANSITION,
    SETTINGS_DRAWER_SHELL_SX,
    SETTINGS_DRAWER_TRANSITION,
    SETTINGS_PANEL_BODY_SX,
    SETTINGS_PANEL_FOOTER_SX,
    SETTINGS_PANEL_HAIRLINE_SX,
    SETTINGS_PANEL_SX,
    SETTINGS_TAB_ROW_SX,
} from "./lobbySettingsStyles";

const MotionBox = motion.create ? motion.create(Box) : motion(Box);

export default function LobbySettingsDrawer({
    open = false,
    activeTab,
    onTabChange,
    onClose,
    actions,
    catalog,
    settingsResetVersion = 0,
}) {
    const resolvedTab = resolveLobbySettingsTab(activeTab);
    const {
        availableMaps,
        queueMode,
        canEditLobbySettings,
        canRefineSelections,
        currentLobbyCode,
        currentUserGender,
        playerCountry,
        isMatchmakingEditingDisabled,
        isReadOnly,
        lobbyMapsToDisplay,
        lobbyOptionsToDisplay,
        lobbyServerLocationsToDisplay,
        locationEditingDisabled,
        mapEditingDisabled,
        membershipTier,
        serverLocations,
        handleLeaderMapUpdate,
        handleLeaderMatchmakingUpdate,
        handleLeaderServerLocationUpdate,
        handleMatchmakingOptionChange,
        onMapClicked,
        onServerLocationClicked,
    } = useLobbySettingsController({
        actions,
        catalog,
        settingsResetVersion,
    });

    useEffect(() => {
        if (!open) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose?.();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, onClose]);

    const locationTotal = Array.isArray(serverLocations)
        ? serverLocations.length
        : 0;
    const locationSelectedCount = (lobbyServerLocationsToDisplay || [])
        .map((locationId) => normalizeLocationId(locationId))
        .filter(Boolean).length;
    const showSelectionFooter =
        resolvedTab === "map-pool" || resolvedTab === "server-locations";
    const lockMatchFiltersViewport = shouldShowPremiumMatchFiltersPrompt({
        membershipTier,
        currentLobbyCode,
        canEditLobbySettings,
    });

    return (
        <>
            <BackdropOverlay
                mountWhenClosed
                open={open}
                onClick={onClose}
                zIndex={999}
                transition={SETTINGS_DRAWER_BACKDROP_TRANSITION}
            />
            <MotionBox
                role="dialog"
                aria-modal="true"
                aria-labelledby="lobby-settings-drawer-title"
                initial={{ x: "100%" }}
                animate={{ x: open ? 0 : "100%" }}
                transition={SETTINGS_DRAWER_TRANSITION}
                sx={{
                    ...SETTINGS_DRAWER_SHELL_SX,
                    pointerEvents: open ? "auto" : "none",
                }}
            >
                <Box sx={SETTINGS_PANEL_SX}>
                    <Box sx={SETTINGS_PANEL_HAIRLINE_SX} />

                    <Box
                        sx={{
                            ...SETTINGS_CONTENT_COLUMN_SX,
                            flexShrink: 0,
                            pt: "1.15rem",
                            pb: isReadOnly ? "0.7rem" : "0.85rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.65rem",
                            [MOBILE_NAV_MQ]: {
                                pt: "0.95rem",
                                pb: isReadOnly ? "0.55rem" : "0.7rem",
                            },
                        }}
                    >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                id="lobby-settings-drawer-title"
                                component="h2"
                                sx={{
                                    color: colors.text.primary,
                                    fontWeight: 600,
                                    fontSize: { xs: "1.15rem", xl: "1.3rem" },
                                    letterSpacing: "0.04em",
                                    lineHeight: 1.15,
                                }}
                            >
                                Matchmaking setup
                            </Typography>
                            {isReadOnly ? (
                                <Typography
                                    role="status"
                                    sx={{
                                        mt: "0.28rem",
                                        fontSize: "0.7rem",
                                        fontWeight: 500,
                                        letterSpacing: "0.03em",
                                        lineHeight: 1.3,
                                        color: "rgba(255,255,255,0.42)",
                                        [MOBILE_NAV_MQ]: {
                                            fontSize: "0.64rem",
                                        },
                                    }}
                                >
                                    Viewing only — the party leader can edit
                                </Typography>
                            ) : null}
                        </Box>
                        {isReadOnly ? (
                            <Box
                                aria-hidden
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "0.28rem",
                                    flexShrink: 0,
                                    height: "1.7rem",
                                    px: "0.5rem",
                                    borderRadius: "999px",
                                    border: `1px solid ${alpha(colors.primary.main, 0.35)}`,
                                    background: alpha(colors.primary.main, 0.08),
                                    [MOBILE_NAV_MQ]: {
                                        px: "0.4rem",
                                    },
                                }}
                            >
                                <LockOutlined
                                    sx={{
                                        fontSize: "0.78rem",
                                        color: colors.accent.soft,
                                    }}
                                />
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: "0.58rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.1em",
                                        textTransform: "uppercase",
                                        color: colors.accent.soft,
                                        lineHeight: 1,
                                        [MOBILE_NAV_MQ]: {
                                            display: "none",
                                        },
                                    }}
                                >
                                    Read only
                                </Typography>
                            </Box>
                        ) : null}
                        <IconButton
                            onClick={onClose}
                            aria-label="Close matchmaking setup"
                            sx={{
                                flexShrink: 0,
                                width: "1.9rem",
                                height: "1.9rem",
                                borderRadius: "0.55rem",
                                color: colors.text.secondary,
                                backgroundColor: "rgba(255, 255, 255, 0.04)",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                                },
                            }}
                        >
                            <Close sx={{ fontSize: "0.95rem" }} />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            ...SETTINGS_CONTENT_COLUMN_SX,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            role="tablist"
                            aria-label="Matchmaking setup sections"
                            sx={SETTINGS_TAB_ROW_SX}
                        >
                            {LOBBY_SETTINGS_TABS.map((tab) => {
                                const isActive = tab.id === resolvedTab;
                                return (
                                    <Box
                                        key={tab.id}
                                        component="button"
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        onClick={() => onTabChange?.(tab.id)}
                                        sx={{
                                            flex: "0 0 auto",
                                            width: "auto",
                                            minWidth: 0,
                                            position: "relative",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            py: "0.7rem",
                                            px: 0,
                                            m: 0,
                                            cursor: "pointer",
                                            border: "none",
                                            fontFamily: "inherit",
                                            background: "transparent",
                                            color: isActive
                                                ? colors.primary.main
                                                : colors.text.secondary,
                                            transition: "color 0.15s ease-in-out",
                                            boxSizing: "border-box",
                                            "& .lobby-settings-tab-chip": {
                                                px: "0.85rem",
                                                py: "0.42rem",
                                                borderRadius: "4px",
                                                backgroundColor: "transparent",
                                                transition:
                                                    "background-color 0.15s ease-in-out, color 0.15s ease-in-out",
                                            },
                                            "&::after": {
                                                content: '""',
                                                position: "absolute",
                                                left: "28%",
                                                width: "44%",
                                                bottom: 0,
                                                height: "2px",
                                                borderRadius: "1px",
                                                backgroundColor: colors.primary.main,
                                                opacity: isActive ? 1 : 0,
                                                transition: "opacity 0.15s ease-in-out",
                                            },
                                            "&:hover": {
                                                color: isActive
                                                    ? colors.primary.main
                                                    : colors.text.primary,
                                            },
                                            "&:hover .lobby-settings-tab-chip": {
                                                backgroundColor: "rgba(255,255,255,0.08)",
                                            },
                                            "&:focus-visible": {
                                                outline: "none",
                                            },
                                            "&:focus-visible .lobby-settings-tab-chip": {
                                                backgroundColor: "rgba(255,255,255,0.1)",
                                                boxShadow: `inset 0 0 0 1px ${alpha(
                                                    colors.primary.main,
                                                    0.45,
                                                )}`,
                                            },
                                            [MOBILE_NAV_MQ]: {
                                                flex: "0 1 auto",
                                                width: "auto",
                                                maxWidth: "100%",
                                                py: "0.55rem",
                                                "& .lobby-settings-tab-chip": {
                                                    display: "inline-flex",
                                                    width: "auto",
                                                    px: "0.4rem",
                                                    py: "0.38rem",
                                                    justifyContent: "center",
                                                    textAlign: "center",
                                                },
                                            },
                                        }}
                                    >
                                        <Box className="lobby-settings-tab-chip">
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: "0.78rem",
                                                    fontWeight: 700,
                                                    letterSpacing: "0.08em",
                                                    textTransform: "uppercase",
                                                    lineHeight: 1,
                                                    whiteSpace: "nowrap",
                                                    color: "inherit",
                                                    [MOBILE_NAV_MQ]: {
                                                        fontSize: "0.68rem",
                                                        letterSpacing: "0.04em",
                                                        whiteSpace: "normal",
                                                        textAlign: "center",
                                                        display: "block",
                                                        lineHeight: 1.2,
                                                    },
                                                }}
                                            >
                                                {tab.label}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    <Box
                        data-lenis-prevent
                        data-lenis-prevent-wheel
                        data-lenis-prevent-touch
                        sx={{
                            ...SETTINGS_PANEL_BODY_SX,
                            ...(resolvedTab === "match-filters" &&
                            lockMatchFiltersViewport
                                ? { overflow: "hidden" }
                                : null),
                        }}
                    >
                        {resolvedTab === "map-pool" && (
                            <MapPoolTab
                                lobbyMapsSelected={lobbyMapsToDisplay}
                                availableMaps={availableMaps}
                                mapEditingDisabled={mapEditingDisabled}
                                canEditLobbySettings={canEditLobbySettings}
                                currentLobbyCode={currentLobbyCode}
                                canRefineSelections={canRefineSelections}
                                onMapClicked={onMapClicked}
                                onLeaderMapUpdate={handleLeaderMapUpdate}
                                queueMode={queueMode}
                            />
                        )}
                        {resolvedTab === "server-locations" && (
                            <ServerLocationsTab
                                selectedServerLocations={
                                    lobbyServerLocationsToDisplay
                                }
                                serverLocations={serverLocations}
                                locationEditingDisabled={locationEditingDisabled}
                                canEditLobbySettings={canEditLobbySettings}
                                currentLobbyCode={currentLobbyCode}
                                canRefineSelections={canRefineSelections}
                                onServerLocationClicked={onServerLocationClicked}
                                onLeaderServerLocationUpdate={
                                    handleLeaderServerLocationUpdate
                                }
                            />
                        )}
                        {resolvedTab === "match-filters" && (
                            <MatchFiltersTab
                                matchmakingOptions={lobbyOptionsToDisplay}
                                onMatchmakingOptionChange={
                                    handleMatchmakingOptionChange
                                }
                                onLeaderMatchmakingUpdate={
                                    handleLeaderMatchmakingUpdate
                                }
                                canEditLobbySettings={canEditLobbySettings}
                                currentLobbyCode={currentLobbyCode}
                                isMatchmakingEditingDisabled={
                                    isMatchmakingEditingDisabled
                                }
                                membershipTier={membershipTier}
                                currentUserGender={currentUserGender}
                                playerCountry={playerCountry}
                                settingsResetVersion={settingsResetVersion}
                            />
                        )}
                    </Box>
                    {showSelectionFooter ? (
                        <Box component="footer" sx={SETTINGS_PANEL_FOOTER_SX}>
                            <SettingsSelectionMeter
                                variant="footer"
                                count={
                                    resolvedTab === "map-pool"
                                        ? lobbyMapsToDisplay.length
                                        : locationSelectedCount
                                }
                                total={
                                    resolvedTab === "map-pool"
                                        ? availableMaps.length
                                        : locationTotal
                                }
                                label={
                                    resolvedTab === "map-pool"
                                        ? "maps selected"
                                        : "regions selected"
                                }
                            />
                        </Box>
                    ) : null}
                </Box>
            </MotionBox>
        </>
    );
}

LobbySettingsDrawer.propTypes = {
    open: PropTypes.bool,
    activeTab: PropTypes.string,
    onTabChange: PropTypes.func,
    onClose: PropTypes.func,
    actions: PropTypes.object.isRequired,
    catalog: PropTypes.object.isRequired,
    settingsResetVersion: PropTypes.number,
};

export function GlobalLobbySettingsDrawer({ actions, catalog }) {
    const { open, activeTab, settingsResetVersion } = useLobbySettingsPanel();

    return (
        <LobbySettingsDrawer
            open={open}
            activeTab={activeTab}
            onTabChange={setLobbySettingsPanelTab}
            onClose={closeLobbySettingsPanel}
            actions={actions}
            catalog={catalog}
            settingsResetVersion={settingsResetVersion}
        />
    );
}

GlobalLobbySettingsDrawer.propTypes = {
    actions: PropTypes.object.isRequired,
    catalog: PropTypes.object.isRequired,
};
